/**
 * Admin API client (Settings area) — talks to mttr/app_api `/api/admin/*`.
 * Gated server-side by the `users.manage` / `companies.manage` capabilities;
 * the token rides in the `access-token` header.
 */

import { ApiError, apiFetch } from "./apiClient";
import type {
  AlertType,
  Cadence,
  ChannelKind,
  NotificationChannel,
  NotificationPreference,
} from "./notificationsApi";

export interface AdminUser {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company_id: number | null;
  company_name: string | null;
  company_type: string | null;
  is_super_user: boolean;
  scu_access_mode: "derived" | "explicit";
  role_id: number | null;
  role_name: string | null;
}

export interface ScuGrant {
  scu_name: string;
  can_read: boolean;
  can_write: boolean;
}

/** Per-user capability override state (migration 126). `effective` = does the
 *  user hold the capability right now; `override` = 1 grant / 0 deny / null =
 *  fall back to the role default. */
export interface PermissionState {
  effective: boolean;
  override: 0 | 1 | null;
}

export interface AdminUserDetail {
  user: AdminUser;
  grants: ScuGrant[];
  effective_scus: string[];
  /** Per-user override state for every toggleable view cap, keyed by
   *  permission_key (migrations 126 + 128). Drives the "View permissions"
   *  controls in the edit-user modal. */
  view_permissions: Record<string, PermissionState>;
  /** pricing.view override state (alias of view_permissions["pricing.view"]),
   *  kept for the original "Can see pricing" control. */
  pricing_view: PermissionState;
}

/** The toggleable view capabilities exposed as per-user + per-role 3-state
 *  overrides. Must mirror OVERRIDABLE_VIEW_CAPS on the server (routes/admin.py);
 *  labels are UI-only. */
export const OVERRIDABLE_VIEW_CAPS: { key: string; label: string }[] = [
  { key: "pricing.view", label: "Pricing & invoice values" },
  { key: "dashboard.fleet_health", label: "Dashboard · Fleet health tile" },
  { key: "dashboard.tag_health", label: "Dashboard · Tag health tile" },
  { key: "dashboard.starlink", label: "Dashboard · Starlink tile" },
  { key: "dashboard.peplink", label: "Dashboard · Peplink tile" },
];

/** One (role, cap) cell of the per-role capability matrix (migration 128).
 *  `baseline` = the migration-owned role_permission grant; `override` = 1 grant
 *  / 0 deny / null (fall back to baseline); `effective` = the resolved value. */
export interface RolePermissionCell {
  permission_key: string;
  baseline: boolean;
  override: 0 | 1 | null;
  effective: boolean;
}

export interface RolePermissionRow {
  role_id: number;
  role_code: string | null;
  role_name: string | null;
  company_type: string | null;
  cells: RolePermissionCell[];
}

export interface RolePermissionMatrix {
  roles: RolePermissionRow[];
  capabilities: string[];
}

export interface SystemRole {
  role_id: number;
  role_code: string | null;
  role_name: string | null;
  company_type: string | null;
  description: string | null;
  permissions: string[];
  /** True for company-scoped custom roles (false for the built-in system
   *  roles). Custom roles carry a `company_id`; system roles have it null. */
  is_custom: boolean;
  company_id: number | null;
}

export interface Permission {
  permission_key: string;
  label: string;
  description: string | null;
}

export interface Company {
  company_id: number;
  company_code: string | null;
  company_name: string | null;
  company_type: string;
  display_timezone: string | null;
  user_count: number;
  /** Departure / Re-arrival cancellation policy (migrations 078/079).
   *  When enabled, a CONFIRMED Departure + its matching RE_ARRIVAL get
   *  is_cancelled=true on the Departures/Arrivals pages if both events
   *  fall in the same cutoff-anchored week-window in this company's
   *  display_timezone. The "default customer" leaves this off and sees
   *  every event as a normal row. */
  departure_cancellation_enabled: boolean;
  /** Day-of-week for the cutoff anchor: 0=Sun..6=Sat. */
  departure_cancellation_cutoff_dow: number;
  /** Hour-of-day for the cutoff anchor: 0..23 (local time). */
  departure_cancellation_cutoff_hour: number;
  /** When true, cancelled rows are hidden entirely from this
   *  company's users (platform always sees them with strikethrough). */
  departure_cancellation_hide: boolean;

  /** Consignment-report config (migrations 088, 090, 092).
   *  `display_abbr` is the vendor short name used in the weekly
   *  consignment invoice (`companyNameAbbr` on every line, plus the
   *  uppercased form in `headerDescription`). Customer rows leave it
   *  NULL. */
  display_abbr: string | null;
  /** Per-customer week cutoff for the consignment report (0=Sun..6=Sat). */
  consignment_cutoff_dow: number;
  /** Per-customer cutoff hour (0..23 local time). */
  consignment_cutoff_hour: number;
  /** Single char emitted as the line `type` on the C-file (default 'C'). */
  consignment_default_type: string;
  /** Fallback `requester` for the report when no per-workcenter row
   *  exists in mttr.consignment_requester. */
  consignment_requester_default: string | null;
}

export interface UpdateUserBody {
  role_id?: number;
  scu_access_mode?: "derived" | "explicit";
  grants?: ScuGrant[];
  first_name?: string;
  last_name?: string;
  /**
   * Where notification subscriptions and the Admin > Mail test send. A blank
   * one means the user silently receives nothing, so it is editable here rather
   * than only at account creation.
   *
   * The server 409s (naming the holder) if another account already has it.
   * Several accounts here legitimately share one person across companies via
   * `name+customer@example.com` aliases.
   */
  email?: string;
}

export interface CreateUserBody {
  company_id: number;
  first_name?: string;
  last_name?: string;
  email: string;
  password: string;
  role_id: number;
  is_super_user?: boolean;
}

export interface CreateCompanyBody {
  company_name: string;
  company_type: "customer" | "vendor";
  display_timezone?: string | null;
  admin?: { first_name?: string; last_name?: string; email: string; password: string };
}

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "AdminApiError";
  }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new AdminApiError(e.message, e.status) : e;
  }
}

// ---- users ----
export interface AdminUsersPage {
  users: AdminUser[];
  /** Total matching users across all pages (for the pager). */
  total: number;
  /** 1-based page index the server resolved from the offset. */
  page: number;
  page_size: number;
}

/** Server-paginated + searched user list. `q` matches name / email / company
 *  name; `limit` is capped server-side at 500 (default 100). */
export async function fetchAdminUsers(
  token: string,
  opts: { q?: string; limit?: number; offset?: number } = {},
): Promise<AdminUsersPage> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset != null) p.set("offset", String(opts.offset));
  const qs = p.toString();
  return request<AdminUsersPage>(`/api/admin/users${qs ? `?${qs}` : ""}`, token);
}
export function fetchAdminUser(uid: number, token: string): Promise<AdminUserDetail> {
  return request<AdminUserDetail>(`/api/admin/users/${uid}`, token);
}
export function updateAdminUser(uid: number, body: UpdateUserBody, token: string): Promise<unknown> {
  return request(`/api/admin/users/${uid}`, token, { method: "POST", body: JSON.stringify(body) });
}
export function createAdminUser(body: CreateUserBody, token: string): Promise<{ user_id: number }> {
  return request<{ user_id: number }>("/api/admin/users", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
/**
 * Set / clear a per-user capability override (migration 126).
 * `effect`: 1 = always grant, 0 = always deny, null = clear (role default).
 * Takes effect on the user's NEXT login.
 */
export function setUserPermissionOverride(
  uid: number,
  permissionKey: string,
  effect: 0 | 1 | null,
  token: string,
): Promise<{ ok: boolean; user_id: number; permission_key: string; state: PermissionState }> {
  return request(`/api/admin/users/${uid}/permissions`, token, {
    method: "POST",
    body: JSON.stringify({ permission_key: permissionKey, effect }),
  });
}

/** Operator reset of another user's password (users.manage + company-scoped).
 *  Writes bcrypt server-side; the plaintext never persists. */
export function resetUserPassword(
  uid: number,
  password: string,
  token: string,
): Promise<{ ok: boolean; user_id: number }> {
  return request(`/api/admin/users/${uid}/password`, token, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// ---- admin-on-behalf-of notifications ----
// Manage a TARGET user's notification channels + subscriptions. Same shapes as
// the self-serve notificationsApi, but keyed on the target uid and gated
// server-side by `users.manage` (+ company scope for non-platform admins).

export async function fetchUserChannels(
  uid: number,
  token: string,
): Promise<NotificationChannel[]> {
  return (
    await request<{ channels: NotificationChannel[] }>(
      `/api/admin/users/${uid}/notifications/channels`,
      token,
    )
  ).channels;
}

export function createUserChannel(
  uid: number,
  body: { kind: ChannelKind; address: string; label?: string | null },
  token: string,
): Promise<NotificationChannel> {
  return request<NotificationChannel>(
    `/api/admin/users/${uid}/notifications/channels`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function deleteUserChannel(
  uid: number,
  channelId: number,
  token: string,
): Promise<{ channel_id: number; deleted: boolean }> {
  return request(
    `/api/admin/users/${uid}/notifications/channels/${channelId}`,
    token,
    { method: "DELETE" },
  );
}

export async function fetchUserPreferences(
  uid: number,
  token: string,
): Promise<NotificationPreference[]> {
  return (
    await request<{ preferences: NotificationPreference[] }>(
      `/api/admin/users/${uid}/notifications/preferences`,
      token,
    )
  ).preferences;
}

export function createUserPreference(
  uid: number,
  body: {
    alert_type: AlertType;
    channel_id: number;
    cadence: Cadence;
    hour_local?: number;
    minute_local?: number;
    dow_mask?: number;
    scu?: string | null;
    day_of_month?: number;
  },
  token: string,
): Promise<NotificationPreference> {
  return request<NotificationPreference>(
    `/api/admin/users/${uid}/notifications/preferences`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function updateUserPreference(
  uid: number,
  preferenceId: number,
  patch: Partial<{
    enabled: boolean;
    cadence: Cadence;
    hour_local: number;
    minute_local: number;
    dow_mask: number;
    channel_id: number;
    day_of_month: number;
    scu: string;
  }>,
  token: string,
): Promise<NotificationPreference> {
  return request<NotificationPreference>(
    `/api/admin/users/${uid}/notifications/preferences/${preferenceId}`,
    token,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export function deleteUserPreference(
  uid: number,
  preferenceId: number,
  token: string,
): Promise<{ preference_id: number; deleted: boolean }> {
  return request(
    `/api/admin/users/${uid}/notifications/preferences/${preferenceId}`,
    token,
    { method: "DELETE" },
  );
}

// ---- roles + permissions ----
export async function fetchRoles(token: string): Promise<SystemRole[]> {
  return (await request<{ roles: SystemRole[] }>("/api/admin/roles", token)).roles;
}
export async function fetchPermissions(token: string): Promise<Permission[]> {
  return (await request<{ permissions: Permission[] }>("/api/admin/permissions", token)).permissions;
}

/** The editable per-role capability grid (migration 128). */
export function fetchRolePermissionMatrix(token: string): Promise<RolePermissionMatrix> {
  return request<RolePermissionMatrix>("/api/admin/roles/permissions-matrix", token);
}

/**
 * Set / clear a per-ROLE capability override (migration 128).
 * `effect`: 1 = always grant, 0 = always deny, null = clear (baseline default).
 * Takes effect on each affected user's NEXT login.
 */
export function setRolePermissionOverride(
  roleId: number,
  permissionKey: string,
  effect: 0 | 1 | null,
  token: string,
): Promise<{ ok: boolean; role_id: number; permission_key: string; effect: 0 | 1 | null }> {
  return request(`/api/admin/roles/${roleId}/permissions`, token, {
    method: "POST",
    body: JSON.stringify({ permission_key: permissionKey, effect }),
  });
}

// ---- companies ----
export async function fetchCompanies(token: string): Promise<Company[]> {
  return (await request<{ companies: Company[] }>("/api/admin/companies", token)).companies;
}
export function createCompany(
  body: CreateCompanyBody,
  token: string,
): Promise<{ company_id: number; admin_user_id: number | null }> {
  return request("/api/admin/companies", token, { method: "POST", body: JSON.stringify(body) });
}
export interface UpdateCompanyBody {
  company_name: string;
  company_type: "customer" | "vendor";
  display_timezone?: string | null;
  departure_cancellation_enabled?: boolean;
  departure_cancellation_cutoff_dow?: number;
  departure_cancellation_cutoff_hour?: number;
  departure_cancellation_hide?: boolean;
  // Consignment-report config (migrations 088, 090, 092)
  display_abbr?: string | null;
  consignment_cutoff_dow?: number;
  consignment_cutoff_hour?: number;
  consignment_default_type?: string;
  consignment_requester_default?: string | null;
}

export function updateCompany(
  cid: number,
  body: UpdateCompanyBody,
  token: string,
): Promise<unknown> {
  return request(`/api/admin/companies/${cid}`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ----- Items admin (mttr.item — critical flag + lead-time override) -----

export interface ItemMetaRow {
  item_id: number;
  sku: string | null;
  name: string | null;
  category_name: string | null;
  oem_no: string | null;
  unit_price: number | null;
  lead_time_days: number;
  lead_time_override_days: number | null;
  lead_time_set_by: string | null;
  lead_time_set_utc: string | null;
  is_critical: boolean;
  critical_set_by: string | null;
  critical_set_utc: string | null;
}

export interface ItemsPage {
  total: number;
  limit: number;
  offset: number;
  items: ItemMetaRow[];
}

export function fetchItemsAdmin(
  token: string,
  opts: {
    q?: string;
    only_critical?: boolean;
    only_overridden?: boolean;
    limit?: number;
    offset?: number;
  } = {},
): Promise<ItemsPage> {
  const p = new URLSearchParams();
  if (opts.q)              p.set("q", opts.q);
  if (opts.only_critical)  p.set("only_critical", "1");
  if (opts.only_overridden) p.set("only_overridden", "1");
  if (opts.limit != null)  p.set("limit", String(opts.limit));
  if (opts.offset != null) p.set("offset", String(opts.offset));
  const qs = p.toString();
  return request(`/api/admin/items${qs ? `?${qs}` : ""}`, token) as Promise<ItemsPage>;
}

export function setItemCritical(
  token: string,
  itemId: number,
  isCritical: boolean,
): Promise<ItemMetaRow> {
  return request(`/api/admin/items/${itemId}/critical`, token, {
    method: "POST",
    body: JSON.stringify({ is_critical: isCritical }),
  }) as Promise<ItemMetaRow>;
}

export function setItemLeadTime(
  token: string,
  itemId: number,
  days: number | null,
): Promise<ItemMetaRow> {
  return request(`/api/admin/items/${itemId}/lead-time`, token, {
    method: "POST",
    body: JSON.stringify({ days }),
  }) as Promise<ItemMetaRow>;
}

/** Common IANA timezones for the company display-timezone picker. */
export const COMMON_TIMEZONES = [
  "America/Chicago",
  "America/New_York",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Halifax",
  "UTC",
];
